import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Globe, ChevronDown } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' }
] as const

export function LanguageSelector() {
  const [isOpen, setIsOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const { language, setLanguage, isRTL } = useLanguage()
  const [dropdownPosition, setDropdownPosition] = useState<'left' | 'right'>(isRTL ? 'right' : 'left')

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0]

  const handleLanguageChange = (langCode: typeof language) => {
    setLanguage(langCode)
    setIsOpen(false)
  }

  // Calculate dropdown position based on viewport
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const dropdownWidth = 128 // w-32 = 128px
      
      if (isRTL) {
        // For RTL: default to right-0, but check if it would overflow left
        const wouldOverflowLeft = buttonRect.right - dropdownWidth < 0
        setDropdownPosition(wouldOverflowLeft ? 'left' : 'right')
      } else {
        // For LTR: default to left-0, but check if it would overflow right
        const wouldOverflowRight = buttonRect.left + dropdownWidth > viewportWidth
        setDropdownPosition(wouldOverflowRight ? 'right' : 'left')
      }
    }
  }, [isOpen, isRTL])

  return (
    <div className="relative">
      <motion.button
        ref={buttonRef}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1.5 px-2.5 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors duration-200 ${
          isRTL ? 'space-x-reverse' : ''
        }`}
        title={currentLanguage.name}
      >
       <span className="text-xs font-medium text-foreground">
          {currentLanguage.code.toUpperCase()}
        </span>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className={`absolute top-full mt-2 w-32 bg-background border border-border rounded-lg shadow-lg z-50 ${
              dropdownPosition === 'right' ? 'right-0' : 'left-0'
            }`}
          >
            <div className="py-2">
              {languages.map((lang) => (
                <motion.button
                  key={lang.code}
                  whileHover={{ backgroundColor: 'var(--secondary)' }}
                  onClick={() => handleLanguageChange(lang.code as any)}
                  className={`w-full flex items-center space-x-2 px-3 py-2 text-left hover:bg-secondary transition-colors duration-200 ${
                    isRTL ? 'space-x-reverse text-right' : 'text-left'
                  } ${
                    language === lang.code ? 'bg-secondary/50' : ''
                  }`}
                  title={lang.name}
                >
                  <span className="text-sm font-medium text-foreground">
                    {lang.name}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
